import { LockClosedIcon } from '@heroicons/react/24/outline';
import type { DpRequest } from '../../../../../shared/dpRequests';
import { formatBrl, formatDuration, formatReferenceMonth } from '../../../../../shared/dpRequests';
import { HEALTH_PLAN_ACTION_LABELS, REQUEST_TYPE_LABELS, describeRequestStatus } from '../../dpRequests.definitions';
import { formatIsoDate } from '../../utils/formatDayOffDates';
import { StatusPill } from '../StatusPill';
import type { DpQueueListProps } from './dpQueueList.types';

function ageLabel(request: DpRequest): string {
    const days = Math.floor((Date.now() - request.statusChangedAt.toDate().getTime()) / (24 * 60 * 60 * 1000));
    if (days <= 0) return 'hoje';
    return days === 1 ? 'há 1 dia' : `há ${days} dias`;
}

function rowTitle(request: DpRequest): string {
    if (request.type === 'ticket') return request.category === 'reserved' ? 'Chamado reservado' : request.subject;
    if (request.type === 'data_update') return `${request.changes.length} alteração(ões)`;
    if (request.type === 'overtime') return `${formatIsoDate(request.date)} · ${formatDuration(request.durationMinutes)}`;
    if (request.type === 'raro_invest') return `${formatReferenceMonth(request.referenceMonth)} · ${formatBrl(request.amount)}`;
    if (request.type === 'health_plan') return HEALTH_PLAN_ACTION_LABELS[request.action];
    return `${request.totalDays.toString().replace('.', ',')} dia(s) de day-off`;
}

export function DpQueueList({ requests, onOpen, emptyText }: DpQueueListProps) {
    if (requests.length === 0) {
        return (
            <p className="rounded-2xl border border-dashed border-raro-gray-200 px-5 py-10 text-center text-sm text-raro-gray-500">
                {emptyText}
            </p>
        );
    }

    return (
        <ul className="overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm divide-y divide-raro-gray-100">
            {requests.map(request => {
                const meta = describeRequestStatus(request);
                const reserved = request.type === 'ticket' && request.category === 'reserved';
                return (
                    <li key={request.id}>
                        <button
                            type="button"
                            onClick={() => onOpen(request.id)}
                            className="flex w-full flex-col gap-2 px-4 py-3.5 text-left transition hover:bg-primary-50/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-primary-500 sm:flex-row sm:items-center sm:gap-4"
                        >
                            <span className="min-w-0 flex-1">
                                <span className="flex items-center gap-2 text-sm font-semibold text-raro-gray-900">
                                    {reserved && <LockClosedIcon className="h-3.5 w-3.5 text-raro-gray-500" aria-label="Reservado" />}
                                    <span className="truncate">{request.collaboratorName || request.collaboratorEmail}</span>
                                </span>
                                <span className="mt-0.5 block truncate text-xs text-raro-gray-500">
                                    {REQUEST_TYPE_LABELS[request.type]} · {rowTitle(request)} · {ageLabel(request)}
                                </span>
                            </span>
                            <StatusPill label={meta.label} tone={meta.tone} />
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
